import type { CreateTestProcedureInput, TestProcedureRow } from '#shared/types';

/** Everything the form edits except the steps, which have their own list state. */
export type ProcedureValues = Omit<CreateTestProcedureInput, 'procedure_steps'>;

/** Whether a procedure covers a whole test or one analyte. */
export type ProcedureLevel = 'test' | 'parameter';

const EMPTY_PROCEDURE: ProcedureValues = {
  title: '',
  test_id: null,
  parameter_id: null,
  method: '',
  principle: '',
  specimen: '',
  container: '',
  sample_volume: '',
  storage: '',
  interferences: '',
  incubation: '',
  wavelength: '',
  temperature: '',
  calculation: '',
  references: '',
};

/** The form's starting values: the saved procedure when editing, blanks when writing one. */
export function initialProcedureValues(record?: TestProcedureRow): ProcedureValues {
  if (!record) return EMPTY_PROCEDURE;
  return {
    title: record.title,
    test_id: record.test_id,
    parameter_id: record.parameter_id,
    method: record.method ?? '',
    principle: record.principle ?? '',
    specimen: record.specimen ?? '',
    container: record.container ?? '',
    sample_volume: record.sample_volume ?? '',
    storage: record.storage ?? '',
    interferences: record.interferences ?? '',
    incubation: record.incubation ?? '',
    wavelength: record.wavelength ?? '',
    temperature: record.temperature ?? '',
    calculation: record.calculation ?? '',
    references: record.references ?? '',
  };
}

/** A saved whole-test procedure opens at test level; anything else at analyte level. */
export function initialProcedureLevel(record?: TestProcedureRow): ProcedureLevel {
  return record?.parameter_id === null ? 'test' : 'parameter';
}

/**
 * Exactly one target, mirroring the CHECK. Switching clears the other side
 * rather than leaving both set, which the server would refuse.
 */
export function clearOtherTarget(level: ProcedureLevel): Partial<ProcedureValues> {
  return level === 'test' ? { parameter_id: null } : { test_id: null };
}

/** A new procedure starts with one empty step to type into. */
export function initialSteps(record?: TestProcedureRow): string[] {
  return record?.procedure_steps ?? [''];
}

export function appendStep(steps: readonly string[]): string[] {
  return [...steps, ''];
}

export function replaceStep(steps: readonly string[], index: number, text: string): string[] {
  return steps.map((step, at) => (at === index ? text : step));
}

export function removeStep(steps: readonly string[], index: number): string[] {
  return steps.filter((_, at) => at !== index);
}
